import gql from 'graphql-tag';
import { COUPON_FRAGMENT } from '../fragments';

const BlindBoxItemFragment = gql`
  fragment BlindBoxItem on BlindBoxItem {
    id
    createdAt
    updatedAt
    blindBox {
      id
    }
    openBoxImageUrl
    blindBoxId
    productVariant {
      id
      sku
      price
      name
      options {
        code
        name
        group {
          code
          name
        }
      }
      product {
        featuredAsset {
          source
        }
      }
      featuredAsset {
        source
      }
    }
    targetId
    type
    baseProbability
    isWishItem
  }
`;

const BlindBoxFragment = gql`
  fragment BlindBox on BlindBox {
    id
    createdAt
    updatedAt
    wishItemId
    price
    name
    description
    img
    blindBoxItems {
      ...BlindBoxItem
    }
  }
  ${BlindBoxItemFragment}
`;

const BlindBoxActivityBoxLinkFragment = gql`
  fragment BlindBoxActivityBoxLink on BlindBoxActivityBoxLink {
    id
    createdAt
    updatedAt
    blindBoxActivity {
      id
    }
    blindBoxActivityId
    blindBox {
      ...BlindBox
    }
    blindBoxId
    maxAssistLimit
    productProbabilities {
      blindBoxItemId
      targetProbability
    }
  }
  ${BlindBoxFragment}
  ${BlindBoxItemFragment}
`;

const AssistGiftRecordFragment = gql`
  fragment AssistGiftRecord on AssistGiftRecord {
    id
    createdAt
    updatedAt
    assistRecord {
      id
    }
    assistRecordId
    assistGift {
      id
      giftImage
      giftName
      giftPrice
      coupon {
        ...Coupon
      }
    }
    assistGiftId
    customer {
      id
    }
    customerId
    isConvertedUser
    currentAssistCount
    receivedAt
  }
  ${COUPON_FRAGMENT}
`;

const BlindBoxOpenRecordFragment = gql`
  fragment BlindBoxOpenRecord on BlindBoxOpenRecord {
    id
    createdAt
    updatedAt
    blindBoxBuyId
    blindBox {
      ...BlindBox
    }
    blindBoxId
    customer {
      id
    }
    customerId
    blindBoxItem {
      ...BlindBoxItem
    }
    resultId
    isWishedItem
    openedAt
    assistCount
    currentAllProbability
    isFreeShipping
  }
  ${BlindBoxFragment}
  ${BlindBoxItemFragment}
`;

/**
 * 获取盲盒活动信息数据
 */
export const fetchBlindBoxActivityDocument = gql`
  query blindBoxActivity($blindBoxActivityId: ID!) {
    blindBoxActivity(blindBoxActivityId: $blindBoxActivityId) {
      id
      createdAt
      updatedAt
      name
      description
      startAt
      endAt
      enabled
      statue
      isFreeShipping
      freeShippingThreshold
      price
      stock
      sold
      baseBlindBoxProbability
      baseBlindBox {
        ...BlindBox
      }
      baseBlindBoxId
      blindBoxActivityBoxLinks {
        ...BlindBoxActivityBoxLink
      }
    }
  }
  ${BlindBoxFragment}
  ${BlindBoxActivityBoxLinkFragment}
`;

/**
 * 获取盲盒预览商品数据
 */
export const fetchBlindBoxActivityPreviewProductDocument = gql`
  query blindBoxActivityPreviewProduct($blindBoxId: ID!, $blindBoxActivityId: ID!) {
    blindBoxActivityPreviewProduct(
      blindBoxId: $blindBoxId
      blindBoxActivityId: $blindBoxActivityId
    ) {
      baseBlindBox {
        ...BlindBox
      }
      wishBlindBox {
        ...BlindBox
      }
    }
  }
  ${BlindBoxFragment}
`;

/**
 * 获取购买盲盒记录列表
 */
export const fetchGetBlindBoxBuysDocument = gql`
  query getBlindBoxBuys($options: BlindBoxBuyListOptions) {
    getBlindBoxBuys(options: $options) {
      items {
        id
        createdAt
        updatedAt
        code
        customerId
        wishBlindBoxItem {
          ...BlindBoxItem
        }
        blindBoxOpenRecords {
          ...BlindBoxOpenRecord
        }
        baseBlindBox {
          ...BlindBox
        }
        baseBlindBoxId
        wishBlindBox {
          ...BlindBox
        }
        pickupOpenRecord {
          ...BlindBoxOpenRecord
        }
        wishBlindBoxId
        paymentMetadata
        status
        payStatus
        paymentAt
        assistLimit
        assistCount
        maxAssistCount
        assistExpireAt
      }
      totalItems
    }
  }
  ${BlindBoxFragment}
  ${BlindBoxItemFragment}
  ${BlindBoxOpenRecordFragment}
`;

/**
 * 获取盲盒订单详情
 */
export const fetchGetBlindBoxBuyDocument = gql`
  query getBlindBoxBuy($blindBoxBuyId: ID!) {
    getBlindBoxBuy(blindBoxBuyId: $blindBoxBuyId) {
      id
      createdAt
      updatedAt
      code
      price
      customerId
      wishBlindBoxItem {
        ...BlindBoxItem
      }
      blindBoxOpenRecords {
        ...BlindBoxOpenRecord
      }
      baseBlindBox {
        ...BlindBox
      }
      baseBlindBoxId
      wishBlindBox {
        ...BlindBox
      }
      pickupOpenRecord {
        ...BlindBoxOpenRecord
      }
      blindBoxActivityId
      wishBlindBoxId
      paymentMetadata
      status
      payStatus
      paymentAt
      assistLimit
      assistCount
      maxAssistCount
      assistExpireAt
      openBoxMode
      canOpen
      blindBoxRefundRecord {
        refundAmount
        refundAt
      }
    }
  }
  ${BlindBoxFragment}
  ${BlindBoxItemFragment}
  ${BlindBoxOpenRecordFragment}
`;

/**
 * 获取助力数据
 */
export const fetchGetBlindBoxBuyByOrderIdDocument = gql`
  query getBlindBoxBuyByOrderId($blindBoxBuyId: ID!) {
    getBlindBoxBuyByOrderId(blindBoxBuyId: $blindBoxBuyId) {
      id
      createdAt
      updatedAt
      code
      blindBoxActivity {
        description
        assistPagePoster {
          backGroundImage {
            ...PictureResource
          }
          backGroundColor
          layoutType
          pictures {
            ...PictureResource
          }
          productGroup {
            menuCollection {
              menuName
              collectionId
            }
            groupType
          }
          productItems {
            showType
            ids
          }
          productCategoryList {
            banner {
              ...PictureResource
            }
            title
            subCategory {
              banner {
                ...PictureResource
              }
              title
            }
          }
        }
        assistPosterType
        openBoxMode
      }
      blindBoxActivityId
      customer {
        id
      }
      customerId
      wishBlindBoxItem {
        ...BlindBoxItem
      }
      blindBoxOpenRecords {
        ...BlindBoxOpenRecord
      }
      baseBlindBox {
        ...BlindBox
      }
      baseBlindBoxId
      wishBlindBox {
        ...BlindBox
      }
      pickupOpenRecord {
        ...BlindBoxOpenRecord
      }
      wishBlindBoxId
      paymentMetadata
      status
      payStatus
      paymentAt
      assistLimit
      assistCount
      maxAssistCount
      assistExpireAt
    }
  }
  ${BlindBoxFragment}
  ${BlindBoxItemFragment}
  ${BlindBoxOpenRecordFragment}
  fragment PictureResource on PictureResource {
    imgHeight
    imgWidth
    imgUrl
    videoUrl
    alt
    jump {
      ...Jump
    }
  }
  fragment Jump on Jump {
    startAxisX
    startAxisY
    endPointAxisX
    endPointAxisY
    jumpType
    jumpValue
    jumpName
  }
`;

/**
 * 用户助力
 */
export const fetchAssistBlindBoxDocument = gql`
  mutation assistBlindBox($blindBoxBuyId: ID!) {
    assistBlindBox(blindBoxBuyId: $blindBoxBuyId) {
      isReward
      isConvertedUser
      assistGiftRecord {
        ...AssistGiftRecord
      }
    }
  }
  ${AssistGiftRecordFragment}
`;

/**
 * 获取可领取的助力奖品
 */
export const fetchGetAssistGiftRecordDocument = gql`
  query getAssistGiftRecord {
    getAssistGiftRecord {
      id
      createdAt
      updatedAt
      assistRecord {
        id
      }
      assistRecordId
      assistGift {
        id
        giftImage
        giftPrice
        giftName
        coupon {
          ...Coupon
        }
      }
      assistGiftId
      customer {
        id
      }
      customerId
      isConvertedUser
      currentAssistCount
      receivedAt
    }
  }
  ${COUPON_FRAGMENT}
`;

/**
 * 领取助力奖品
 */
export const fetchReceiveAssistGiftDocument = gql`
  mutation receiveAssistGift($assistGiftRecordId: ID!) {
    receiveAssistGift(assistGiftRecordId: $assistGiftRecordId) {
      isReceived
      assistGiftRecord {
        ...AssistGiftRecord
      }
      gift {
        id
      }
    }
  }
  ${AssistGiftRecordFragment}
`;

/**
 * 申请退款
 */
export const fetchRefundBlindBoxDocument = gql`
  mutation refundBlindBox($blindBoxBuyId: ID!) {
    refundBlindBox(blindBoxBuyId: $blindBoxBuyId) {
      id
    }
  }
`;

/**
 * 获取最接近开盒的助力中盒子订单
 */
export const fetchGetNearestAssistBlindBoxDocument = gql`
  query getNearestAssistBlindBox {
    getNearestAssistBlindBox {
      id
      status
      assistCount
      assistLimit
    }
  }
`;

/**
 * 获取剩余可购买信息
 */
export const fetchBlindBoxUserRemainingCountDocument = gql`
  query blindBoxUserRemainingCount {
    blindBoxUserRemainingCount {
      purchaseLimitPeriod
      purchaseLimit
      remainingCount
      limitPeriodCount
    }
  }
`;

/**
 * 获取剩余可助力信息
 */
export const fetchBlindBoxUserAssistRemainingCountDocument = gql`
  query blindBoxUserAssistRemainingCount {
    blindBoxUserAssistRemainingCount {
      assistLimitPeriod
      assistLimit
      remainingCount
      limitPeriodCount
    }
  }
`;

/**
 * 抽出心愿商品用户跑马灯
 */
export const fetchWishBlindBoxUserListDocument = gql`
  query wishBlindBoxUserList {
    wishBlindBoxUserList {
      id
      customer {
        lastName
        phoneNumber
        customFields {
          headPortrait
          isModified
        }
      }
    }
  }
`;

/**
 * 购买盲盒并自动开盒
 */
export const fetchPurchaseBlindBoxDocument = gql`
  mutation purchaseBlindBox(
    $blindBoxActivityId: ID!
    $wishBlindBoxId: ID!
    $paymentType: WeChatPaymentType
  ) {
    purchaseBlindBox(
      blindBoxActivityId: $blindBoxActivityId
      wishBlindBoxId: $wishBlindBoxId
      paymentType: $paymentType
    ) {
      isSkipPay
      status
      appId
      timeStamp
      nonceStr
      package
      signType
      paySign
      orderCode
    }
  }
`;

/**
 * 根据订单号获取盲盒开盒记录
 */
export const fetchGetBlindBoxOpenRecordByOrderCodeDocument = gql`
  query getBlindBoxOpenRecordByOrderCode($orderCode: String!) {
    getBlindBoxOpenRecordByOrderCode(orderCode: $orderCode) {
      id
      isWishedItem
      blindBoxItem {
        ...BlindBoxItem
      }
      isFreeShipping
      blindBoxBuyId
      wishBlindBox {
        ...BlindBox
      }
      blindBoxBuy {
        code
        assistLimit
        assistExpireAt
        assistCount
        wishBlindBoxItem {
          ...BlindBoxItem
        }
      }
    }
  }
  ${BlindBoxFragment}
  ${BlindBoxItemFragment}
`;

/**
 * 再次开盒
 */
export const fetchReOpenBlindBoxDocument = gql`
  mutation reOpenBlindBox($blindBoxBuyId: ID!) {
    reOpenBlindBox(blindBoxBuyId: $blindBoxBuyId) {
      id
      isWishedItem
      blindBoxItem {
        ...BlindBoxItem
      }
      isFreeShipping
      blindBoxBuyId
      blindBoxBuy {
        code
        assistLimit
        assistExpireAt
        assistCount
        wishBlindBoxItem {
          ...BlindBoxItem
        }
      }
      wishBlindBox {
        ...BlindBox
      }
    }
  }
  ${BlindBoxFragment}
  ${BlindBoxItemFragment}
`;

/**
 * 盲盒提货记录上报
 */
export const fetchBlindBoxPickUpDocument = gql`
  mutation blindBoxPickUp($blindBoxOpenRecordId: ID!, $blindBoxBuyId: ID!) {
    blindBoxPickUp(blindBoxOpenRecordId: $blindBoxOpenRecordId, blindBoxBuyId: $blindBoxBuyId) {
      id
    }
  }
`;

/**
 * 预约盲盒活动
 */
export const fetchBlindBoxActivityBookingDocument = gql`
  mutation blindBoxActivityBooking($blindBoxActivityId: ID!) {
    blindBoxActivityBooking(blindBoxActivityId: $blindBoxActivityId)
  }
`;

/**
 * 查询是否已预约盲盒活动
 */
export const fetchBlindBoxActivityIsBookingDocument = gql`
  query blindBoxActivityIsBooking($blindBoxActivityId: ID!) {
    blindBoxActivityIsBooking(blindBoxActivityId: $blindBoxActivityId)
  }
`;

/**
 * 查询用户是否已助力
 */
export const fetchCustomerIsAssistDocument = gql`
  query customerIsAssist($blindBoxBuyId: ID!) {
    customerIsAssist(blindBoxBuyId: $blindBoxBuyId)
  }
`;

/**
 * 获取盲盒订单
 */
export const fetchTakeBlindBoxOrderDocument = gql`
  mutation takeBlindBoxOrder($blindBoxBuyId: ID!) {
    takeBlindBoxOrder(blindBoxBuyId: $blindBoxBuyId) {
      id
    }
  }
`;
